import { AbstractAnalyticsTracker } from '@inter-ikea-kompis/analytics';
import { PriceUtility, TotalPriceCalculator } from '@inter-ikea-kompis/utilities';
import { IpexMomentEnum } from '@insights/insights-data-provider';
const moduleId = 'configuration_summary';
/**
 * Tracks events from Configuration Summary and sends them to a reporter.
 *
 * @event click When kiosk main CTA button is clicked.
 * @event save_planner When save design button is clicked.
 *
 * @tracker packages/component-add-to-bag/src/trackers/AddToBagTracker.ts
 * @tracker packages/component-financial-services/src/trackers/FinancialServicesTracker.ts
 * @tracker packages/component-configuration-summary/src/trackers/ConfigurationSummaryConfirmationCardTracker.ts
 */
export default class ConfigurationSummaryTracker extends AbstractAnalyticsTracker {
    constructor(options) {
        super({ moduleId, ...options });
        this.context = options.context;
    }
    /**
     * Call to report when clicking to buy in store.
     */
    sendKioskMainCTAButtonClick() {
        const payload = {
            common: { component: moduleId },
            data: {
                action: 'buy_in_store'
            },
            context: this.context
        };
        this.getReporter().report({
            event: 'click',
            ipexMoment: IpexMomentEnum.giveMe,
            payload
        });
    }
    /**
     * Call to report when save design has been clicked.
     *
     * @param options ...
     * @param options.type The way the design was saved. Sharing through VPC, or saving to profile.
     * @param options.vpcCode The VPC code when using options.type 'Share'.
     */
    sendSaveDesign(options) {
        this.getReporter().saveDesign(options, this.context);
    }
    /**
     * Call to report when products have been added to the shopping cart.
     *
     * @param shoppingItems ...
     * @param shoppingProducts ...
     * @param settings ...
     * @param unavailableItems ...
     * @param designId ...
     */
    sendAddToCart(shoppingItems, shoppingProducts, settings, unavailableItems, designId) {
        const eventData = this.getEventData(shoppingItems, shoppingProducts, settings, designId, unavailableItems);
        this.getReporter().addToCart(eventData);
    }
    /**
     * Call to report when products have been added to the shopping list.
     *
     * @param shoppingItems ...
     * @param shoppingProducts ...
     * @param settings ...
     * @param designId ...
     */
    sendAddToList(shoppingItems, shoppingProducts, settings, designId) {
        const eventData = this.getEventData(shoppingItems, shoppingProducts, settings, designId);
        this.getReporter().addToWishList(eventData);
    }
    getEventData(shoppingItems, shoppingProducts, settings, designId, unavailable) {
        const eventData = {
            currency: settings.localisation.currencyCode,
            items: this.getArticleData(shoppingItems, shoppingProducts, settings)
        };
        if (unavailable) {
            eventData.unavailableItems = this.getArticleData(unavailable, shoppingProducts, settings);
        }
        if (designId) {
            eventData.designId = designId;
        }
        if (this.context) {
            eventData.payload = { context: this.context };
        }
        return eventData;
    }
    /**
     * Maps shopping items to article data, with price calculation.
     *
     * @param shoppingItems Items to be converted to articles.
     * @param shoppingProducts Products data for price calculation.
     * @param settings Used for price calculation formatting.
     * @returns Array of article data with ID, price, and quantity.
     */
    getArticleData(shoppingItems, shoppingProducts, settings) {
        return shoppingItems.map((shoppingItem) => {
            const shoppingProduct = shoppingProducts.find((sp) => sp.product?.content?.ruItemNo === shoppingItem.id);
            if (!shoppingProduct) {
                throw new Error(`Could not find shopping product matching id '${shoppingItem.id}'!`);
            }
            const priceSummary = TotalPriceCalculator.calculate(
            // Setting quantity to one so we get the price for just one product.
            [{ ...shoppingProduct, quantity: 1 }], settings.localisation.dateFormat);
            const price = PriceUtility.getPrice(priceSummary);
            return { id: shoppingItem.id, price, qty: shoppingItem.quantity };
        });
    }
}
//# sourceMappingURL=ConfigurationSummaryTracker.js.map